import facebookIconColor from "@/assets/icons/facebook.svg";
import facebookIcon from "@/assets/icons/fbGrayscale.svg";
import instagramIcon from "@/assets/icons/igGrayscale.svg";
import fbVerifiedIcon from "@/assets/icons/fbVerified.svg";
import igVerifiedIcon from "@/assets/icons/igVerified.svg";
import { SearchBrandItem } from "@/screens/analysis/types";
import { RiAddLargeLine, RiCheckLine, RiExternalLinkLine } from "@remixicon/react";
import { SqButton } from "@/components/squircle-ui/button";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// Utility to format numbers like 1.1M, 120k for numbers >= 10,000
function formatNumberCompact(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + "M";
  } else if (num >= 10000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + "k";
  } else {
    return num.toLocaleString();
  }
}

const BrandRow = ({
  brand,
  onClick,
  isSelected = false,
  isAdded = false,
}: {
  brand: SearchBrandItem;
  onClick: () => void;
  isSelected?: boolean;
  isAdded?: boolean;
}) => {
  return (
    <div
      className={`group flex px-[9px] py-[6px] items-center gap-[12px] ${isSelected ? "bg-selected-gray" : "hover:bg-[#FAFAFA]"}`}
    >
      {/*Profile Picture*/}
      <div className="flex items-center">
        <div className="w-[24px] h-[24px] rounded-[7.2px] overflow-hidden object-contain shrink-0">
          <img src={brand.profile_picture_url} alt={brand.page_name} />
        </div>
      </div>
      {/*Brand Details*/}
      <div className="flex-1">
        <div className="w-full flex flex-col gap-1">
          <div>
            <span className="text-brand-primary-800 text-[14px] font-normal leading-none">
              {brand.page_name}
            </span>
          </div>
          <div className="flex items-center gap-[6px]">
            <div>
              <img
                src={facebookIcon}
                alt="Facebook"
                className="w-[11px] h-[11px] grayscale"
              />
            </div>
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                @
              </span>
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                {brand.fb_username}
              </span>
            </div>
            {brand.fb_verified && (
              <div>
                <img src={fbVerifiedIcon} alt="verified" className="w-[9px] h-[9px]" />
              </div>
            )}
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                {brand.fb_likes !== null ? formatNumberCompact(brand.fb_likes) : "-"}
              </span>
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                &nbsp;follow this
              </span>
            </div>
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                .
              </span>
            </div>
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                {brand.category}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-[6px]">
            <div>
              <img
                src={instagramIcon}
                alt="Instagram"
                className="w-[11px] h-[11px] grayscale"
              />
            </div>
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                @
              </span>
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                {brand.ig_username}
              </span>
            </div>
            {brand.ig_verified && (
              <div>
                <img src={igVerifiedIcon} alt="verified" className="w-[9px] h-[9px]" />
              </div>
            )}
            <div className="flex">
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                {brand.ig_followers !== null
                  ? formatNumberCompact(brand.ig_followers)
                  : "-"}
              </span>
              <span className="text-brand-primary-600 text-[11px] font-normal leading-none">
                &nbsp;followers
              </span>
            </div>
          </div>
        </div>
      </div>
      {/*Ad Library Button*/}
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <SqButton
              variant="outline"
              size={"sm"}
              onClick={(e) => {
                e.stopPropagation();
                if (brand.ad_library_url) {
                  window.open(brand.ad_library_url, "_blank");
                }
              }}
              className="hidden group-hover:flex bg-white rounded-[8px]"
            >
              <div className="flex items-center gap-[7px]">
                <img
                  src={facebookIconColor}
                  alt="Facebook"
                  className="w-[14px] h-[14px]"
                />
              </div>
              <RiExternalLinkLine className="w-[14px] h-[14px] text-brand-primary-400" />
            </SqButton>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center">
            <p>Open in Ad library</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger>
            <SqButton
              size={"sm"}
              variant={"outline"}
              onClick={onClick}
              disabled={isAdded}
              className={cn(
                "hidden group-hover:flex w-[28px] bg-white rounded-[8px]",
                isSelected && "flex",
              )}
            >
              {isSelected ? (
                <RiCheckLine size={14} className="text-brand-orange shrink-0" />
              ) : (
                <RiAddLargeLine size={14} className="text-brand-primary-400 shrink-0" />
              )}
            </SqButton>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center">
            <p>
              {isAdded
                ? "You've already added this brand."
                : isSelected
                  ? "Deselect Brand"
                  : "Select Brand"}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default BrandRow;
